/**
 * Textarea field component for multi-line text input.
 */

import * as React from "react";
import { cn } from "~/lib/utils";
import { FieldWrapper, getFieldAriaIds } from "./FieldWrapper";
import type { FieldProps } from "./types";

export function TextareaField({
  field,
  value,
  touched,
  error,
  label,
  placeholder,
  description,
  extendedDescription,
  onChange,
  onBlur,
}: FieldProps) {
  const hasError = touched && !!error;
  const { descriptionId, extendedDescriptionId, errorId } = getFieldAriaIds(field.name);

  // Build aria-describedby based on available content
  const ariaDescribedByParts: string[] = [];
  if (hasError) {
    ariaDescribedByParts.push(errorId);
  } else {
    if (description) ariaDescribedByParts.push(descriptionId);
    if (extendedDescription) ariaDescribedByParts.push(extendedDescriptionId);
  }
  const ariaDescribedBy = ariaDescribedByParts.length > 0 ? ariaDescribedByParts.join(" ") : undefined;

  return (
    <FieldWrapper
      name={field.name}
      label={label}
      required={field.validation?.required}
      hasError={hasError}
      error={error}
      description={description}
      extendedDescription={extendedDescription}
    >
      <textarea
        id={field.name}
        value={value as string}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          onChange(field.name, e.target.value)
        }
        onBlur={() => onBlur(field.name)}
        placeholder={placeholder}
        aria-invalid={hasError}
        aria-describedby={ariaDescribedBy}
        aria-required={field.validation?.required}
        className={cn(
          "flex min-h-[100px] w-full rounded-md border bg-background px-3 py-2 text-sm transition-colors",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50 resize-none",
          hasError
            ? "border-destructive focus-visible:ring-destructive"
            : "border-input focus-visible:ring-ring"
        )}
      />
    </FieldWrapper>
  );
}
